import { api } from "../api";

/**
 * Push-Anmeldung. Zwei Ebenen:
 *  - Der Service Worker zeigt Meldungen auch bei geschlossenem Tab.
 *  - Bei offenem Tab reicht die Notification-API direkt.
 */

export type PushState = "nicht-unterstuetzt" | "aus" | "verweigert" | "an";

/**
 * Der VAPID-Schlüssel kommt base64url-kodiert. Der Rückgabetyp ist bewusst
 * ArrayBuffer: TypeScript unterscheidet seit 5.7 zwischen ArrayBuffer und
 * SharedArrayBuffer, und subscribe() akzeptiert nur ersteren.
 */
function base64ToBuffer(base64: string): ArrayBuffer {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(padded);
  const buffer = new ArrayBuffer(raw.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) view[i] = raw.charCodeAt(i);
  return buffer;
}

export function pushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function hinweiseMoeglich(): boolean {
  return "Notification" in window;
}

export type HinweisStand = "nicht-unterstuetzt" | "offen" | "verweigert" | "erlaubt";

export function hinweisStand(): HinweisStand {
  if (!("Notification" in window)) return "nicht-unterstuetzt";
  if (Notification.permission === "granted") return "erlaubt";
  if (Notification.permission === "denied") return "verweigert";
  return "offen";
}

/**
 * Fragt nur nach der Anzeigeerlaubnis — ohne Service Worker und Abonnement.
 * Das reicht für Hinweise, solange die Seite offen ist, und ist die kleinere
 * Bitte: Wer erst einmal sehen will, wie sich das anfühlt, muss nicht gleich
 * ein Push-Abonnement einrichten.
 */
export async function erlaubnisHolen(): Promise<HinweisStand> {
  if (!("Notification" in window)) return "nicht-unterstuetzt";
  if (Notification.permission === "granted") return "erlaubt";
  const antwort = await Notification.requestPermission();
  return antwort === "granted" ? "erlaubt" : antwort === "denied" ? "verweigert" : "offen";
}

export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return "nicht-unterstuetzt";
  if (Notification.permission === "denied") return "verweigert";
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  return sub ? "an" : "aus";
}

export async function enablePush(): Promise<{ ok: boolean; grund?: string }> {
  if (!pushSupported()) {
    return { ok: false, grund: "Dieser Browser kann keine Push-Nachrichten. Auf dem iPhone muss die Seite dafür zum Home-Bildschirm hinzugefügt werden." };
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, grund: "Du hast Benachrichtigungen abgelehnt. Das lässt sich nur in den Browser-Einstellungen zurücknehmen." };
  }

  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;

  const { publicKey } = await api.pushKey();
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      // Ohne diese Zusage verweigern Chrome und Firefox das Abonnement.
      userVisibleOnly: true,
      applicationServerKey: base64ToBuffer(publicKey),
    }));

  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    return { ok: false, grund: "Der Browser hat ein unvollständiges Abonnement geliefert." };
  }

  await api.pushSubscribe({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    label: navigator.userAgent.slice(0, 60),
  });
  return { ok: true };
}

export async function disablePush(): Promise<void> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await api.pushUnsubscribe(sub.endpoint).catch(() => {});
  await sub.unsubscribe();
}

/** Meldung bei offener Seite — dafür braucht es keinen Service Worker. */
/**
 * Bubble am App-Icon (Badging API). Greift nur, wenn Maildeck als App
 * installiert ist — auf dem iPhone zusätzlich erst mit Mitteilungs-Erlaubnis.
 * Im normalen Tab steht die Zahl stattdessen im Titel.
 */
export function appBadgeSetzen(anzahl: number): void {
  document.title = anzahl > 0 ? `(${anzahl}) Maildeck` : "Maildeck";
  const nav = navigator as Navigator & {
    setAppBadge?: (n?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  if (!nav.setAppBadge || !nav.clearAppBadge) return;
  (anzahl > 0 ? nav.setAppBadge(anzahl) : nav.clearAppBadge()).catch(() => { /* nicht installiert oder nicht erlaubt */ });
}

export function showLocalNotification(title: string, body: string): void {
  if (!("Notification" in window) || Notification.permission !== "granted") return;

  /*
   * hasFocus statt hidden: Ein Fenster, das hinter einem anderen liegt, gilt
   * als sichtbar, obwohl niemand hinsieht. Genau dann ist die Meldung nützlich.
   * Wer die Seite gerade im Blick hat, bekommt stattdessen den Hinweis in der App.
   */
  if (document.hasFocus()) return;

  const meldung = new Notification(title, {
    body,
    tag: "neue-mail",
    icon: "/icon-192.png",
    badge: "/icon-192.png",
  });
  meldung.onclick = () => {
    window.focus();
    meldung.close();
  };
}

export interface LiveEvent {
  type: string;
  payload: { count?: number; account?: string; notable?: { from: string; subject: string; priority: string | null }[] };
}

/**
 * Hält den Ereignisstrom offen. EventSource verbindet sich nach einem Abbruch
 * selbstständig neu — deshalb hier keine eigene Wiederverbindungslogik.
 */
export function connectEvents(onEvent: (e: LiveEvent) => void): () => void {
  const source = new EventSource("/api/events", { withCredentials: true });

  const handle = (type: string) => (ev: MessageEvent) => {
    try {
      onEvent({ type, payload: JSON.parse(ev.data) });
    } catch {
      onEvent({ type, payload: {} });
    }
  };

  for (const type of ["neue-mail", "eingeordnet", "beleg", "sync", "aufgabe"]) {
    source.addEventListener(type, handle(type));
  }

  return () => source.close();
}
